import { InfoCircleOutlined } from '@ant-design/icons';
import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import Alert from 'antd/lib/alert';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import Collapse from 'antd/lib/collapse';
import DatePicker from 'antd/lib/date-picker';
import Divider from 'antd/lib/divider';
import Dropdown from 'antd/lib/dropdown';
import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import InputNumber from 'antd/lib/input-number';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal/Modal';
import Radio from 'antd/lib/radio';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { theme } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { differenceInMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { useAuth } from '../../../domain/auth/useAuth';
import { formatToMinutesAndHours, getComputerFormattedTime } from '../../../domain/date/date';
import { toAllowedStringLength } from '../../../domain/common/formattersToAllowedValueLength';
import { createEventBatch, cancelEvent, cancelEventBatch, fetchEventsBatch, isCompleted, updateEvent, updateEventBatch } from '../../../domain/event';
import { IEvent, IEventFormState, SIGNAL_LEVEL_OPTIONS, SliceType, SliceTypeLabel } from '../../../domain/event/interface';
import { UserType } from '../../../domain/user/interface';
import { IProgram } from '../../../domain/program/interface';
import { FORM_ACTION, IAffectedSites, IEventFormProps, toBatchRequest, ViewEvent } from '../eventInterface';
import { isPastDate, isStarted } from '../utils';
import { EventConfirmationModal } from './EventConfirmationModal';
import { CalendarEventPreview } from './CalendarEventPreview';
import { ResourceCollapsedList } from './ResourceCollapsedList/ResourceCollapsedList';
import { ProgramSelector } from '../../../components/selectors/ProgramSelector/ProgramSelector';

const preparedBatchEventData = (batchEvents: IEvent[]) =>
    (batchEvents || []).map(event => ({
        company: event?.site?.company?.company_name!,
        company_id: event?.site?.company?.company_id,
        site: event?.site?.site_name!,
        site_timezone: event?.site?.site_timezone!,
        id: `${event?.site?.site_id!}`,
    }));

const CONFIRMATION_TITLE = {
    [FORM_ACTION.CANCEL_BATCH]: 'cancel',
    [FORM_ACTION.CANCEL]: 'cancel',
    [FORM_ACTION.CANCEL_BATCH_IN_COMPANY]: 'cancel',
    [FORM_ACTION.UPDATE_BATCH]: 'update',
    [FORM_ACTION.UPDATE]: 'update',
    [FORM_ACTION.UPDATE_BATCH_IN_COMPANY]: 'update',
    [FORM_ACTION.CREATE]: 'save',
};

export const EventBySiteFormModal = (props: IEventFormProps) => {
    const { token: themeColors } = theme.useToken();

    const { notification } = App.useApp();
    const { event, resources, onClose, isEditMode } = props;

    const [loading, setLoading] = useState<boolean>(false);
    const [batchEvents, setBatchEvents] = useState<IAffectedSites[]>([]);
    const viewEvent = useMemo(() => new ViewEvent(event), [event]);
    const [affectedSites, setAffectedSites] = useState<IAffectedSites[]>(resources);
    const [formAction, setFormAction] = useState<FORM_ACTION | null>(null);
    const [form] = Form.useForm();
    const auth = useAuth();
    const customerCompanyId = auth?.user?.user_type === UserType.CUSTOMER ? auth.user.company_id : undefined;

    const isNoEndTimeEvent = Form.useWatch('no_end_time', form);
    const isTestEvent = Form.useWatch('event_test', form);
    const isEmergencyEvent = Form.useWatch('event_emergency_generator_allowed', form);
    const isVoluntaryEvent = Form.useWatch('event_voluntary', form);

    const preEventDuration = Form.useWatch('event_pre_duration_hours', form);
    const secondaryPreEventDuration = Form.useWatch('event_secondary_duration_hours', form);
    const eventDuration = Form.useWatch('event_duration_hours', form);
    const postEventDuration = Form.useWatch('event_post_duration_hours', form);
    const event_datetime_start = Form.useWatch('event_datetime_start_local', form);

    const [allResources, setAllResources] = useState<IAffectedSites[]>([]);
    const [isLoadingAllResources, setIsLoadingAllResources] = useState<boolean>(true);

    useEffect(() => {
        const batchId = viewEvent?.event_batch;

        if (!batchId) {
            setAllResources(resources);
            setIsLoadingAllResources(false);
            return;
        }

        if (batchEvents?.length) {
            setAllResources(batchEvents);
            setIsLoadingAllResources(false);
        }
    }, [viewEvent, batchEvents]);

    const [state, setState] = useState<IEventFormState>({});

    const handleProgramSelect = (program: IProgram) => {
        setState(prevState => ({
            ...prevState,
            program: { program_name: program.name, program_id: program.program_id },
        }));
        form.setFieldsValue({
            program_id: program.program_id,
            program_name: program.name,
        });
    };

    useEffect(() => {
        viewEvent && form.setFieldsValue(viewEvent);

        const batchId = viewEvent.event_batch;
        if (batchId) {
            (async () => {
                const data = await fetchEventsBatch({
                    batchId,
                    include: 'site,company',
                    companyId: customerCompanyId,
                });
                setBatchEvents(preparedBatchEventData(data));
            })();
        }
    }, [viewEvent]);

    useEffect(() => setAffectedSites(resources), [resources]);

    const warning = useMemo(
        () =>
            event.event_cancelled || isCompleted(event as IEvent) ? (
                <Alert
                    showIcon
                    message="Warning"
                    description="Cannot edit an event which has been canceled or has already completed."
                    type="warning"
                    style={{ display: 'flex', alignItems: 'center' }}
                />
            ) : null,
        [event]
    );

    function handleNoEndTimeChange(value: boolean) {
        if (value) {
            // we need to clear previous selected values for post and secondary durations and for event slice
            form.setFieldsValue({
                event_post_duration_hours: 0,
                event_secondary_duration_hours: 0,
                event_slice: { type: SliceType.NONE, amount: 0 },
            });

            viewEvent.event_duration_hours = 12;
        } else {
            viewEvent.event_duration_hours = 1;
        }
    }

    function setDurationToNow() {
        if (isCompleted(event as IEvent)) {
            form.setFieldsValue({ event_duration_hours: viewEvent.event_duration_hours });
            return;
        }

        const nowInSiteTimezone = toZonedTime(new Date(), event.site!.site_timezone);

        const diffInMinutes = differenceInMinutes(nowInSiteTimezone, event_datetime_start);

        const durationHours = diffInMinutes > 0 ? Math.round((diffInMinutes / 60) * 100) / 100 : 0;

        form.setFieldsValue({ event_duration_hours: durationHours });
    }

    function resourcesValidator({ getFieldValue }: { getFieldValue: Function }) {
        return {
            validator(_: any, value: any) {
                return resources?.length
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please select at least one site from the left section of the calendar!'));
            },
        };
    }

    //create + update event
    const onEventFormFinish = () => {
        if (isEditMode) {
            updateEventWithConfirmation();
        } else {
            createEventWithConfirmation();
        }
    };

    //create event
    const createEventWithConfirmation = () => setFormAction(FORM_ACTION.CREATE);
    const createBatchAction = async () => {
        setLoading(true);
        try {
            const preparedEvent = await form.validateFields();
            const { event, sites } = toBatchRequest(preparedEvent, resources);
            await createEventBatch(event, sites, customerCompanyId);
            notification.info({ key: 'event-save-info', message: 'Events saved' });
            onClose(preparedEvent);
        } catch (err: any) {
            notification.error({ key: 'event-save-error', message: err.message || 'Cannot save events!' });
        }
        setLoading(false);
    };

    //update event
    const updateEventWithConfirmation = () => {
        setFormAction(FORM_ACTION.UPDATE);
        setAffectedSites(resources);
    };
    const updateEventAction = async () => {
        const eventId = event.event_id!;
        setLoading(true);
        try {
            const preparedEvent = await form.validateFields();
            const { event } = toBatchRequest(preparedEvent) as any;
            await updateEvent(eventId, event);
            notification.info({ key: 'event-update-info', message: 'Event updated' });
            onClose(event);
        } catch (err: any) {
            notification.error({ key: 'event-update-error', message: err.message || 'Cannot update event!' });
        }
        setLoading(false);
    };

    // cancel one event
    const cancelEventWithConfirmation = () => {
        setAffectedSites(resources);
        setFormAction(FORM_ACTION.CANCEL);
    };
    const cancelEventAction = async () => {
        const eventId = event.event_id;

        if (!eventId) return;

        try {
            await cancelEvent(eventId);
            notification.info({ key: 'event-cancel-info', message: 'Event canceled' });
        } catch (error: any) {
            notification.error({ key: 'event-cancel-error', message: error.message || 'Cannot cancel event!' });
        }
        onClose(event);
    };

    // update batch event
    const updateBatchWithConfirmation = () => {
        form.validateFields().then(() => {
            setFormAction(FORM_ACTION.UPDATE_BATCH);
            setAffectedSites(batchEvents);
        });
    };
    const updateBatchAction = async (companyId?: number) => {
        try {
            const formValues = form.getFieldsValue();
            const { event } = toBatchRequest(formValues);
            await updateEventBatch(viewEvent.event_batch!, event, companyId);
            notification.info({ key: 'batch-update-info', message: 'Batch updated' });
            onClose(event);
        } catch (error: any) {
            notification.error({ key: 'batch-update-error', message: error.message || 'Cannot update batch!' });
        }
    };

    // cancel batch events
    const cancelBatchWithConfirmation = () => {
        setAffectedSites(batchEvents);
        setFormAction(FORM_ACTION.CANCEL_BATCH);
    };
    const cancelBatchAction = async (companyId?: number) => {
        try {
            await cancelEventBatch(viewEvent.event_batch!, companyId);
            notification.info({ key: 'batch-cancel-info', message: 'Batch canceled' });
            onClose(viewEvent);
        } catch (error: any) {
            notification.error({ key: 'batch-cancel-error', message: error.message || 'Cannot cancel batch!' });
        }
    };

    const cancelBatchInCompanyWithConfirmation = () => {
        setFormAction(FORM_ACTION.CANCEL_BATCH_IN_COMPANY);
        const affectedSitesInCompany = batchEvents.filter(site => site.company_id === event.site?.company_id);
        setAffectedSites(affectedSitesInCompany);
    };

    const updateBatchInCompanyWithConfirmation = () => {
        form.validateFields().then(() => {
            setFormAction(FORM_ACTION.UPDATE_BATCH_IN_COMPANY);
            const affectedSitesInCompany = batchEvents.filter(site => site.company_id === event.site?.company_id);
            setAffectedSites(affectedSitesInCompany);
        });
    };

    const eventConfirmationModalAction = {
        [FORM_ACTION.CANCEL_BATCH]: () => {
            cancelBatchAction(customerCompanyId);
            closeEventConfirmationModal();
        },
        [FORM_ACTION.CANCEL]: () => {
            cancelEventAction();
            closeEventConfirmationModal();
        },
        [FORM_ACTION.CANCEL_BATCH_IN_COMPANY]: () => {
            const companyId = auth?.user?.isAdmin() ? event.site?.company_id : customerCompanyId;
            cancelBatchAction(companyId);
            closeEventConfirmationModal();
        },
        [FORM_ACTION.UPDATE_BATCH]: () => {
            updateBatchAction(customerCompanyId);
            closeEventConfirmationModal();
        },
        [FORM_ACTION.UPDATE_BATCH_IN_COMPANY]: () => {
            const companyId = auth?.user?.isAdmin() ? event.site?.company_id : customerCompanyId;
            updateBatchAction(companyId);
            closeEventConfirmationModal();
        },

        [FORM_ACTION.UPDATE]: () => {
            updateEventAction();
            closeEventConfirmationModal();
        },
        [FORM_ACTION.CREATE]: () => {
            createBatchAction();
            closeEventConfirmationModal();
        },
    };

    const closeEventConfirmationModal = () => setFormAction(null);

    const closeDialog = () => onClose();

    const cancelActionsMenu = () => {
        const companyName = toAllowedStringLength(event.site?.company?.company_name!, 20);
        const companySitesCount = batchEvents.filter(site => site.company_id === event.site?.company_id).length;

        return (
            <Menu>
                <Menu.Item key="cancel-batch-events" onClick={cancelBatchWithConfirmation}>
                    Cancel batch ({batchEvents.length})
                </Menu.Item>
                {auth?.user?.isAdmin() && companySitesCount > 1 && (
                    <Menu.Item key="cancel-batch-in-company-events" onClick={cancelBatchInCompanyWithConfirmation}>
                        Cancel in <strong>{companyName}</strong>({companySitesCount})
                    </Menu.Item>
                )}
            </Menu>
        );
    };

    const updateActionsMenu = () => {
        const companyName = toAllowedStringLength(event.site?.company?.company_name!, 20);
        const companySitesCount = batchEvents.filter(site => site.company_id === event.site?.company_id).length;

        return (
            <Menu>
                <Menu.Item key="update-batch-events" onClick={updateBatchWithConfirmation}>
                    Update batch ({batchEvents.length})
                </Menu.Item>
                {auth?.user?.isAdmin() && companySitesCount > 1 && (
                    <Menu.Item key="update-batch-in-company-events" onClick={updateBatchInCompanyWithConfirmation}>
                        Update in <strong>{companyName}</strong> ({companySitesCount})
                    </Menu.Item>
                )}
            </Menu>
        );
    };

    return (
        <Modal
            open
            destroyOnHidden
            title={isEditMode ? 'Update event' : 'New event'}
            onCancel={closeDialog}
            footer={[
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                    <Button key="event-form-cancel" data-cy="close-dialog" onClick={closeDialog}>
                        Close
                    </Button>

                    {!warning && (
                        <>
                            {isEditMode ? (
                                <>
                                    {batchEvents.length > 1 ? (
                                        <>
                                            {!isStarted(event as IEvent) && (
                                                <Dropdown.Button
                                                    onClick={cancelEventWithConfirmation}
                                                    overlay={cancelActionsMenu}
                                                    loading={loading}
                                                    key="cancel-event-actions"
                                                >
                                                    Cancel Event
                                                </Dropdown.Button>
                                            )}
                                            <Dropdown.Button
                                                onClick={form.submit}
                                                overlay={updateActionsMenu}
                                                loading={loading}
                                                key="update-event-actions"
                                            >
                                                Update
                                            </Dropdown.Button>
                                        </>
                                    ) : (
                                        <>
                                            {!isStarted(event as IEvent) && (
                                                <Button
                                                    onClick={cancelEventWithConfirmation}
                                                    loading={loading}
                                                    key="cancel-event-action"
                                                    data-cy="cancel-event"
                                                >
                                                    Cancel Event
                                                </Button>
                                            )}
                                            <Button onClick={form.submit} loading={loading} key="update-event-action" data-cy="update-event">
                                                Update
                                            </Button>
                                        </>
                                    )}
                                </>
                            ) : (
                                <Button data-cy="save-event" key="event-form-submit" type="primary" loading={loading} onClick={form.submit}>
                                    Save ({resources.length})
                                </Button>
                            )}
                        </>
                    )}
                </Space>,
            ]}
            className="event-modal"
            data-cy="event-by-site"
        >
            <div className="event-form-container content-background" data-cy="warning-msg">
                {warning && (
                    <>
                        {warning}
                        <br />
                    </>
                )}

                <Form form={form} name="event-form" preserve={false} layout="vertical" onFinish={onEventFormFinish} initialValues={viewEvent}>
                    <Row>
                        <Col xs={24}>
                            <Form.Item name="resources" label={<Typography.Text strong>Resources</Typography.Text>} rules={[resourcesValidator]}>
                                <ResourceCollapsedList
                                    resources={allResources}
                                    eventDatetime={event_datetime_start}
                                    loading={isLoadingAllResources}
                                    showTimezone
                                />
                            </Form.Item>

                            <Form.Item label={<Typography.Text strong>Pre Event duration</Typography.Text>}>
                                <Space>
                                    <Form.Item name="event_pre_duration_hours" noStyle>
                                        <InputNumber data-cy="event-pre-duration" min={0} max={4} step={0.25} size="large" />
                                    </Form.Item>
                                    <Typography.Text>hours</Typography.Text>
                                </Space>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Form.Item
                                        name="event_datetime_start_local"
                                        required
                                        rules={[{ required: true, message: 'Please select date!' }]}
                                        label={
                                            <>
                                                <Typography.Text strong>Event start&nbsp;</Typography.Text>
                                                <Tooltip title="The event will be dispatched to start at the site's local timezone">
                                                    <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <DatePicker data-cy="date-picker" format="DD/MMM/YYYY HH:mm" showTime={{ format: 'HH:mm' }} size="large" />
                                    </Form.Item>
                                    <Form.Item dependencies={['event_datetime_start_local']} style={{ marginBottom: 0 }}>
                                        {({ getFieldValue }) => {
                                            const selectedDate = getFieldValue('event_datetime_start_local');

                                            return (
                                                selectedDate &&
                                                isPastDate(getFieldValue('event_datetime_start_local')) && (
                                                    <Typography.Text type="secondary">
                                                        <WarningOutlined style={{ color: '#faad14' }} /> Event started in the past.
                                                    </Typography.Text>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <Form.Item
                                name="no_end_time"
                                label={
                                    <>
                                        <Typography.Text strong>No End Time&nbsp;</Typography.Text>
                                        <Tooltip
                                            title={`The event will be dispatched for 12h. ${isEditMode ? 'To complete this event please disable toggle and set duration time' : ''}`}
                                        >
                                            <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                        </Tooltip>
                                    </>
                                }
                                valuePropName="checked"
                                style={{ paddingBottom: '9px' }}
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} onChange={handleNoEndTimeChange} />
                            </Form.Item>
                        </Col>
                        {!isNoEndTimeEvent ? (
                            <Col xs={12}>
                                <Form.Item
                                    label={
                                        <>
                                            <Typography.Text strong>Event duration</Typography.Text>{' '}
                                            {isEditMode && isStarted(event as IEvent) && (
                                                <Button key="end-now-button" size="small" type="link" onClick={setDurationToNow}>
                                                    End Now
                                                </Button>
                                            )}
                                        </>
                                    }
                                >
                                    <Space>
                                        <Form.Item name="event_duration_hours" noStyle>
                                            <InputNumber data-cy="event-duration" min={0} max={12} step={0.25} size="large" />
                                        </Form.Item>
                                        <Typography.Text>hours</Typography.Text>
                                    </Space>
                                </Form.Item>
                            </Col>
                        ) : (
                            <Form.Item name="event_duration_hours" hidden>
                                <InputNumber />
                            </Form.Item>
                        )}
                    </Row>
                    <Row>
                        <Col xs={24}>
                            {!isEditMode && (
                                <Form.Item
                                    required={!isEditMode}
                                    label={<Typography.Text strong>Associate with Program</Typography.Text>}
                                    name="program_id"
                                    rules={[{ required: !isEditMode, message: 'Please select program!' }]}
                                >
                                    <ProgramSelector
                                        handleProgram={handleProgramSelect}
                                        selectedProgram={state?.program?.program_id || null}
                                        excludeUtilityCustomerPrograms={
                                            auth?.user?.user_type === UserType.CUSTOMER || auth?.user?.user_type === UserType.OPERATOR
                                        }
                                    />
                                </Form.Item>
                            )}
                        </Col>
                    </Row>

                    {!isEditMode && (
                        <Collapse
                            key={isNoEndTimeEvent ? 'disabled' : 'enabled'}
                            accordion
                            size="small"
                            collapsible={isNoEndTimeEvent && 'disabled'}
                            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        >
                            <Collapse.Panel key="1" header="Slice">
                                <FormItem
                                    wrapperCol={{ span: 24 }}
                                    label={<Typography.Text strong>Slice Type</Typography.Text>}
                                    name={['event_slice', 'type']}
                                >
                                    <Radio.Group>
                                        {Object.entries(SliceTypeLabel).map(([value, label]) => (
                                            <Radio key={value} value={value} disabled={value === SliceType.KW}>
                                                {label}
                                            </Radio>
                                        ))}
                                    </Radio.Group>
                                </FormItem>
                                <FormItem label={<Typography.Text strong>Slice Amount</Typography.Text>} name={['event_slice', 'amount']}>
                                    <InputNumber min={0} size="large" />
                                </FormItem>
                            </Collapse.Panel>
                        </Collapse>
                    )}

                    <Divider plain>Advanced</Divider>

                    <Row>
                        <Col xs={24}>
                            <Form.Item label={<Typography.Text strong>OADR Signal Level</Typography.Text>} name="event_signal">
                                <Select
                                    placeholder="Please select signal level"
                                    options={SIGNAL_LEVEL_OPTIONS}
                                    style={{ width: '175px' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item label={<Typography.Text strong>Post Event</Typography.Text>}>
                                <Space>
                                    <Form.Item noStyle name="event_post_duration_hours">
                                        <InputNumber min={0} max={4} step={0.25} size="large" disabled={isNoEndTimeEvent} />
                                    </Form.Item>
                                    <Typography.Text>hours</Typography.Text>
                                </Space>
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item label={<Typography.Text strong>Secondary Pre Event</Typography.Text>}>
                                <Space>
                                    <Form.Item name="event_secondary_duration_hours" noStyle>
                                        <InputNumber min={0} max={4} step={0.25} size="large" disabled={isNoEndTimeEvent} />
                                    </Form.Item>
                                    <Typography.Text>hours</Typography.Text>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Item
                                name="event_test"
                                label={
                                    <Typography.Text strong>
                                        Communications Test <br /> (no load drop)
                                    </Typography.Text>
                                }
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item
                                name="event_emergency_generator_allowed"
                                label={<Typography.Text strong>Emergency Generators Allowed</Typography.Text>}
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                            <Form.Item
                                name="event_voluntary"
                                label={<Typography.Text strong>Voluntary Event</Typography.Text>}
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <CalendarEventPreview
                        isTest={isTestEvent}
                        isEmergency={isEmergencyEvent}
                        isVoluntary={isVoluntaryEvent}
                        isNoEndTime={isNoEndTimeEvent}
                        preEventDurationHours={preEventDuration}
                        eventDurationHours={eventDuration}
                        postEventDurationHours={postEventDuration}
                        secondaryPreEventDurationHours={secondaryPreEventDuration}
                    />
                </Form>

                {formAction && (
                    <EventConfirmationModal
                        action={formAction}
                        handleConfirm={eventConfirmationModalAction[formAction]}
                        handleClose={closeEventConfirmationModal}
                        modalContent={
                            <ConfirmationModalContent
                                event={{
                                    ...form.getFieldsValue(),
                                    ...([FORM_ACTION.CANCEL, FORM_ACTION.CANCEL_BATCH, FORM_ACTION.CANCEL_BATCH_IN_COMPANY].includes(formAction) && {
                                        event_datetime_start_local: event.event_datetime_start,
                                    }),
                                }}
                                affectedSites={affectedSites}
                                actionName={CONFIRMATION_TITLE[formAction]}
                            />
                        }
                    />
                )}
            </div>
        </Modal>
    );
};

interface IConfirmationModalContent {
    event: ViewEvent;
    affectedSites: IAffectedSites[];
    actionName: string;
}

export const ConfirmationModalContent = ({ event, affectedSites, actionName }: IConfirmationModalContent) => {
    const numberOfEvents = affectedSites?.length || 1;
    const eventSpelling = `event${numberOfEvents > 1 ? 's' : ''}`;
    const event_duration_in_minutes = event.event_duration_hours * 60;
    const event_pre_duration_in_minutes = event.event_pre_duration_hours * 60;

    return (
        <>
            <Typography>
                You are going to {actionName} <strong>{numberOfEvents}</strong> {eventSpelling}
            </Typography>
            <Typography>
                {eventSpelling} start{numberOfEvents > 1 ? '' : 's'} at site's local time:{' '}
                <strong>{getComputerFormattedTime(event.event_datetime_start_local!, 'dd/MMM/yyyy hh:mm a')}</strong>
            </Typography>
            {Boolean(event_pre_duration_in_minutes) && (
                <Typography>
                    {eventSpelling} pre duration: <strong>{formatToMinutesAndHours(event_pre_duration_in_minutes)}</strong>.
                </Typography>
            )}
            <Typography>
                {eventSpelling} duration: <strong>{!event.no_end_time ? formatToMinutesAndHours(event_duration_in_minutes) : 'unknown'}</strong>.
            </Typography>
        </>
    );
};
